import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { PhotoUpload } from '@/components/profile/PhotoUpload';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { AppHeader } from '@/components/layout/AppHeader';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { photoSafetyService } from '@/services/photoSafetyService';

export const ProfilePhotosPage: React.FC = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [uploadedPhotos, setUploadedPhotos] = useState<{ id: string; key: string; status: string }[]>([]);

  // Preload the ML model when the page mounts
  useEffect(() => {
    console.log('🚀 [Photos] Preloading photo safety model in background...');
    photoSafetyService.preloadModels().catch(error => {
      console.warn('⚠️ [Photos] Model preload failed (non-blocking):', error);
    });
  }, []);

  const handlePhotosUploaded = useCallback((photos: { id: string; key: string; status: string }[]) => {
    console.log('📸 [Photos] Photos uploaded callback received:', photos);
    console.log('📸 [Photos] Setting uploadedPhotos state to:', photos);
    setUploadedPhotos(photos);
  }, []);

  // Save photos to profile mutation
  const savePhotosMutation = useMutation({
    mutationFn: async (photos: { id: string; key: string; status: string }[]) => {
      console.log('🚀 [Photos] savePhotosMutation.mutationFn called with photos:', photos);
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';

      // Convert photos to API format
      // In production, photos will be served via https://r2.hallpass.app
      // In local dev, via local API endpoint
      const baseUrl = import.meta.env.PROD
        ? `${apiUrl}/api/photos/serve`
        : 'http://localhost:8787/api/photos/serve';
      const photoData = photos.map((p, index) => ({
        url: `${baseUrl}/${p.key}`,
        order: index + 1,
        scanStatus: p.status as 'pending' | 'safe' | 'warning' | 'blocked',
      }));

      console.log('🚀 [Photos] Sending PATCH to /api/profiles/me with photoData:', photoData);

      const response = await fetch(`${apiUrl}/api/profiles/me`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ photos: photoData }),
      });

      console.log('🚀 [Photos] PATCH response status:', response.status);
      if (!response.ok) throw new Error('Failed to save photos');
      const result = await response.json();
      console.log('🚀 [Photos] PATCH response body:', result);
      return result;
    },
    onSuccess: () => {
      console.log('✅ [Photos] savePhotosMutation.onSuccess called - navigating to /privacy-setup');
      toast.success('Photos saved!');
      navigate('/privacy-setup');
    },
    onError: (error) => {
      console.error('❌ [Photos] savePhotosMutation.onError called:', error);
      toast.error('Failed to save photos. Please try again.');
    },
  });

  const handleContinue = () => {
    console.log('🔘 [Photos] handleContinue clicked');
    console.log('🔘 [Photos] Current uploadedPhotos state:', uploadedPhotos);
    console.log('🔘 [Photos] uploadedPhotos.length:', uploadedPhotos.length);
    console.log('🔘 [Photos] About to call savePhotosMutation.mutate()');
    savePhotosMutation.mutate(uploadedPhotos);
    console.log('🔘 [Photos] savePhotosMutation.mutate() called');
  };

  const handleBack = () => {
    navigate(-1);
  };

  // TODO: Re-enable photo requirement once R2 storage is configured in local dev
  // const hasSafePhotos = uploadedPhotos.some(photo => photo.status === 'safe');
  const hasSafePhotos = uploadedPhotos.length > 0; // Accept any uploaded photo for now
  console.log('🔍 [Photos] Render - uploadedPhotos:', uploadedPhotos, 'hasSafePhotos:', hasSafePhotos);
  console.log('🔍 [Photos] Render - Button disabled?', !hasSafePhotos || savePhotosMutation.isPending);

  return (
    <>
      <AppHeader />
      <div className="min-h-screen bg-brand-background flex items-center justify-center p-4 pt-20">
        <div className="w-full max-w-2xl">
          <Card className="shadow-xl bg-brand-surface border-brand-border">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold text-white">
              Add Your Photos
            </CardTitle>
            <CardDescription className="text-lg mt-2 text-brand-muted">
              Upload up to 5 photos to showcase your personality. Your first photo will be your primary photo.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Photo Upload Component */}
            <PhotoUpload
              onPhotosUploaded={handlePhotosUploaded}
              maxPhotos={5}
            />

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-4">
              <Button
                onClick={handleBack}
                variant="ghost"
                className="flex items-center gap-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-full px-6"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>

              <Button
                onClick={handleContinue}
                disabled={!hasSafePhotos || savePhotosMutation.isPending}
                className="bg-brand-primary hover:bg-brand-primary/90 hover:scale-105 text-white flex items-center gap-2 px-8 rounded-full shadow-lg shadow-brand-primary/20 transition-transform"
              >
                {savePhotosMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>

            {!hasSafePhotos && (
              <p className="text-sm text-brand-muted text-center">
                Upload at least one photo to continue
              </p>
            )}
          </CardContent>
        </Card>

        {/* Helper Text */}
        <div className="mt-6 text-center space-y-2">
          <p className="text-sm text-gray-400">
            <strong className="text-white">Photo Tips:</strong> Use clear, recent photos where your face is visible.
          </p>
          <p className="text-sm text-gray-400">
            Avoid photos with students, classroom settings, or school-identifiable content.
          </p>
          </div>
        </div>
      </div>
    </>
  );
};
